import * as React from "react";

import { Box, Rating, type RatingProps, useTheme } from "@mui/material";

import type { Theme } from "@mui/material/styles";
import { StarIcon } from "@phosphor-icons/react";

export interface TutredRatingProps extends Omit<RatingProps, "icon" | "emptyIcon"> {
  iconSize?: number;
  filledColor?: string;
  emptyColor?: string;
  emptyOpacity?: number;
  useOutlineIcon?: boolean;
  precision?: number;
}

function resolveColor(theme: Theme, colorString: string): string {
  const parts = colorString.split(".");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let value: any = theme.palette;

  for (const part of parts) {
    value = value?.[part];
    if (value === undefined) {
      return colorString;
    }
  }

  return typeof value === "string" ? value : colorString;
}

const TutredRating = React.forwardRef<HTMLSpanElement, TutredRatingProps>(
  function TutredRating(
    {
      iconSize = 36,
      emptyColor = "text.disabled",
      filledColor = "primary.main",
      emptyOpacity = 1,
      useOutlineIcon = false,
      precision = 0.5,
      sx,
      ...rest
    },
    ref,
  ) {
    const theme = useTheme();
    const resolvedFilledColor = resolveColor(theme, filledColor);
    const resolvedEmptyColor = resolveColor(theme, emptyColor);

    return (
      <Rating
        ref={ref}
        precision={precision}
        icon={
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: resolvedFilledColor,
              lineHeight: 0,
            }}
          >
            <StarIcon weight="fill" size={iconSize} color="currentColor" />
          </Box>
        }
        emptyIcon={
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: resolvedEmptyColor,
              lineHeight: 0,
              opacity: emptyOpacity,
            }}
          >
            <StarIcon
              weight={useOutlineIcon ? "regular" : "fill"}
              size={iconSize}
              color="currentColor"
            />
          </Box>
        }
        sx={[
          {
            "& .MuiRating-icon": {
              fontSize: iconSize,
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...rest}
      />
    );
  },
);

export default TutredRating;
